import { useMemo, useState } from "react";
import * as api from "../api";

export const useSessionManager = (
  selectedPatient,
  fetchPatientDetails,
  sessions,
  sortType
) => {
  const [newSession, setNewSession] = useState({
    session_type: "Diagnostic",
    notes: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [displayLimit, setDisplayLimit] = useState(10);

  const filteredSessions = useMemo(() => {
    if (!sessions?.length) {
      return [];
    }

    if (sortType === "desc") {
      sessions.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else {
      sessions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    return sessions.filter((session) => {
      if (!searchQuery.trim()) return true;

      const query = searchQuery.toLowerCase();
      return (
        session.session_type.toLowerCase().includes(query) ||
        session.notes?.toLowerCase().includes(query) ||
        session.id.toString().includes(query) ||
        new Date(session.session_date)
          .toLocaleString()
          .toLowerCase()
          .includes(query)
      );
    });
  }, [searchQuery, sessions, sortType]);

  const hasMore = !sessions?.length
    ? false
    : filteredSessions.length > displayLimit;

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const loadMore = () => {
    setDisplayLimit((prevLimit) => prevLimit + 10);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewSession((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPatient) {
      setError("Please select a patient first.");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const sessionToCreate = {
        ...newSession,
        patient_id: selectedPatient.id,
      };
      await api.createSession(sessionToCreate);
      setNewSession({ session_type: "Diagnostic", notes: "" });
      setShowForm(false);
      if (fetchPatientDetails) {
        fetchPatientDetails(selectedPatient.id);
      }
    } catch (err) {
      console.error("Error creating session:", err);
      setError(err.response?.data?.detail || "Failed to create session");
    }
    setIsLoading(false);
  };

  return {
    newSession,
    isLoading,
    error,
    showForm,
    setShowForm,
    searchQuery,
    handleSearchChange,
    filteredSessions,
    hasMore,
    loadMore,
    displayedSessions: filteredSessions.slice(0, displayLimit),
    handleInputChange,
    handleSubmit,
  };
};
